import { Link, useNavigate } from 'react-router';
import { Bell, Search } from 'lucide-react';
import { clsx } from '../ds/clsx';
import { Icon } from '../ds/components';
import { mobileTabs, type NavKey } from './navigation';
import styles from './MobileChrome.module.css';

export interface MobileHeaderProps {
  title: string;
  /** Route of the "‹" back link; with a back link the header has no search/bell (EM7, EM8). */
  back?: string | undefined;
  /** Search and bell (default true). */
  actions?: boolean | undefined;
  unread: number;
  notificationsOpen: boolean;
  onOpenSearch: () => void;
  onToggleNotifications: () => void;
  bellRef?: React.RefObject<HTMLButtonElement | null> | undefined;
}

/** Mobile page head (Shell 10, Responsive Regeln): title, search and bell — or "‹ Titel" on sub-pages. */
export function MobileHeader({
  title,
  back,
  actions = true,
  unread,
  notificationsOpen,
  onOpenSearch,
  onToggleNotifications,
  bellRef,
}: MobileHeaderProps) {
  return (
    <header className={styles.header}>
      {back ? (
        <Link to={back} className={styles.back} aria-label="Zurück">
          ‹
        </Link>
      ) : null}
      <h1 className={clsx(styles.title, back && styles.titleWithBack)}>{title}</h1>
      {!back && actions ? (
        <span className={styles.actions}>
          <button type="button" className={styles.iconButton} aria-label="Suche" onClick={onOpenSearch}>
            <span className={styles.iconFrame}>
              <Icon icon={Search} size={18} />
            </span>
          </button>
          <button
            ref={bellRef}
            type="button"
            className={styles.iconButton}
            aria-label="Benachrichtigungen"
            aria-haspopup="dialog"
            aria-expanded={notificationsOpen}
            onClick={onToggleNotifications}
          >
            <span className={clsx(styles.iconFrame, notificationsOpen && styles.iconFrameActive)}>
              <Icon icon={Bell} size={18} />
              {unread > 0 ? (
                <span className={styles.badge} aria-label={`${unread} ungelesen`}>
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : null}
            </span>
          </button>
        </span>
      ) : null}
    </header>
  );
}

export interface MobileTabBarProps {
  active: NavKey | null;
  /** Open tasks (D-38): since „Tasks“ moved to „Mehr“ the count sits on the „Mehr“ tab (hidden at 0). */
  taskCount: number;
}

const primaryTabs: ReadonlySet<string> = new Set(['home', 'contacts', 'calendar', 'properties']);

/**
 * Bottom tab bar (Shell 10, D-314): Start · Kontakte · Kalender · Objekte · Mehr, the same for every role; „Tasks“
 * is the first entry on „Mehr“, whose tab carries the open-task count.
 */
export function MobileTabBar({ active, taskCount }: MobileTabBarProps) {
  const navigate = useNavigate();
  const activeTab: NavKey | null = active === null ? null : primaryTabs.has(active) ? active : 'more';
  return (
    <nav className={styles.tabBar} aria-label="Hauptnavigation">
      {mobileTabs.map((tab) => {
        const isActive = tab.key === activeTab;
        const badge = tab.key === 'more' && taskCount > 0 ? taskCount : null;
        return (
          <button
            key={tab.key}
            type="button"
            className={styles.tab}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => navigate(tab.href)}
          >
            <span
              className={clsx(styles.tabIndicator, isActive && styles.tabIndicatorActive)}
              aria-hidden="true"
            />
            <span className={clsx(styles.tabLabel, isActive && styles.tabLabelActive)}>
              {tab.label}
              {badge !== null ? (
                <span
                  className={clsx(styles.tabBadge, isActive && styles.tabBadgeActive)}
                  aria-label={`${badge} offene Tasks`}
                >
                  {badge}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
